<template>
  <div>
    <!-- Modernized Trigger Button -->
    <v-btn 
      color="primary" 
      :loading="loading" 
      @click="fetch" 
      text
      class="font-weight-black text-uppercase tracking-widest rounded-lg px-4 transition-fast-in-fast-out h-44"
    >
      <v-icon left size="16">fas fa-file-export</v-icon>
      Exportar
    </v-btn>

    <!-- Modernized Download Dialog -->
    <v-dialog v-model="dataReady" max-width="400" transition="scale-transition">
      <v-card class="modern-card overflow-hidden rounded-xl">
        <!-- Premium Header -->
        <div class="bg-grey-light pa-6 border-bottom">
          <div class="d-flex align-center">
            <v-avatar color="primary" size="40" class="mr-4 shadow-sm border-white-sm">
              <v-icon color="white" small>fas fa-download</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-black primary--text lh-1">Exportar Datos</div>
              <div class="caption grey--text font-weight-bold text-uppercase tracking-wider mt-1">Preparando archivo CSV</div>
            </div>
          </div>
        </div>

        <v-card-text class="text-center pa-8">
          <v-avatar color="primary lighten-5" size="80" class="mb-6">
            <v-icon color="primary" size="40">fas fa-file-csv</v-icon>
          </v-avatar>
          <div class="body-1 primary--text font-weight-bold mb-2">¡Todo listo para descargar!</div>
          <div class="caption grey--text px-4 mb-6">Su archivo de cotizaciones ha sido procesado y está listo para ser guardado.</div>

          <vue-json-to-csv
            :json-data="salesNotes"
            :labels="labels"
            :csv-title="csvTitle"
            :separator="';'"
            @success="val => handleSuccess(val)"
            @error="val => handleError(val)">
            <v-btn 
              color="success" 
              block 
              large 
              elevation="0"
              class="font-weight-black text-none rounded-lg h-12"
            >
              <v-icon left>fas fa-download</v-icon>
              Descargar Archivo
            </v-btn>
          </vue-json-to-csv>
        </v-card-text>

        <v-divider class="mx-8 opacity-10"></v-divider>
        <div class="pa-4 text-center">
          <v-btn text small color="grey" class="text-none font-weight-bold" @click="dataReady = false">Cerrar</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import notifications from '@/utils/notifications'
import {fetchSalesNote} from '@/api/salesNotes'
import VueJsonToCsv from 'vue-json-to-csv'
import moment from 'moment'
import numeral from 'numeral'
import rejectedReasons from '@/utils/rejectedReasons'

export default {
  name: "ExportSalesNotes",
  props: ['filter', 'options', 'search'],
  components: { VueJsonToCsv },
  data() {
    return {
      loading: false,
      salesNotes: [],
      dataReady: false,
      origins: this.$store.getters.getOrigins,
      labels: {
        id: { title: 'Nro' },
        created_at: { title: 'Creada' },
        name: { title: 'Proyecto' },
        origin: { title: 'Origen' },
        closing_date: { title: 'Fecha de Cierre' },
        customer_name: { title: 'Cliente' },
        tipo: { title: 'Tipo' },
        industry: { title: 'Industria' },
        rubro: { title: 'Rubro' },
        seller: { title: 'Vendedor' },
        seller_bussiness_unit: { title: 'Unidad de Negocio' },
        total_sale: { title: 'Venta' },
        work_order: { title: 'Orden de Trabajo' },
        status: { title: 'Estado' },
        reposicion: { title: 'Reposición' },
        rejected_at: { title: 'Fecha de rechazo' },
        rejected_by_full_name: { title: 'Rechazada por' },
        rejected_reason: { title: 'Motivo Rechazo' },
        rejected_note: { title: 'Observaciones Rechazo' },
      },
      csvTitle: `export_cotizaciones_${moment(new Date().toISOString()).format('DD-MM-yyyy_HH:mm')}`
    }
  },
  methods: {
    async fetch() {
      this.loading = true
      var created_before = ''
      var created_after = ''
      var closing_before = ''
      var closing_after = ''
      if (this.filter.created_after) created_after = moment(this.filter.created_after).utc().format('yyyy-MM-DD HH:mm')
      if (this.filter.created_before) created_before = moment(this.filter.created_before).utc().add(1439, 'minutes').format('yyyy-MM-DD HH:mm')
      if (this.filter.closing_after) closing_after = moment(this.filter.closing_after).format('yyyy-MM-DD')
      if (this.filter.closing_before) closing_before = moment(this.filter.closing_before).format('yyyy-MM-DD')
      
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      
      if (!this.$can('readAll', 'SalesNote')) {
        const user = this.$store.getters.getUser
        this.filter.seller = user.id
      }
      
      await fetchSalesNote(
        sortDesc[0] ? `${sortBy[0]}` : `-${sortBy[0]}`,
        this.search, // Added search from props
        created_after,
        created_before,
        closing_after,
        closing_before,
        this.filter.customer.id,
        this.filter.seller,
        '',
        1,
        -1,
        this.filter.status ? this.filter.status : '',
        this.filter.origin || '',
        this.filter.reposicion || '')
        .then((data) => {
          this.salesNotes = data.results
          this.salesNotes.map(salesNote => {
            salesNote.created_at = moment(salesNote.created_at).format("DD/MM/yyyy HH:mm")
            salesNote.closing_date = salesNote.closing_date ? moment(salesNote.closing_date).format("DD/MM/yyyy") : ''
            salesNote.customer_name = salesNote.sales_note_customer.name
            salesNote.tipo = salesNote.sales_note_customer.tipo
            salesNote.industry = salesNote.sales_note_customer.industry
            salesNote.rubro = salesNote.sales_note_customer.rubro
            salesNote.seller = `${salesNote.sales_note_customer.seller.first_name} ${salesNote.sales_note_customer.seller.last_name}`
            salesNote.total_sale = numeral(salesNote.totals.total).format('0')
            salesNote.work_order = salesNote.work_order ? salesNote.work_order : ''
            salesNote.origin = this.origins.find(o => o.id == salesNote.origin)?.name || ''
            salesNote.seller_bussiness_unit = salesNote.seller_bussiness_unit || ''
            salesNote.reposicion = salesNote.reposicion ? 'Sí' : 'No'
            
            switch (salesNote.status) {
              case 'pending':
                salesNote.status = 'Abierta'
                break
              case 'confirmed':
                salesNote.status = 'Ganada'
                break
              case 'rejected':
                salesNote.status = 'Cerrada'
                salesNote.rejected_at = moment(salesNote.rejected_at).format("DD/MM/yyyy")
                salesNote.rejected_reason = rejectedReasons.find(r => r.value == salesNote.rejected_reason)?.text
            }
          })
          this.dataReady = true
        })
        .catch((error) => {
          notifications.showError('Error', error)
        })
        .finally(() => this.loading = false)
    },
    handleSuccess(val) {
      notifications.showSuccess('Notas de Venta Exportadas')
      this.dataReady = false
    },
    handleError(val) {
      notifications.showError('Error Exportando Notas de Venta')
      this.dataReady = false
    }
  }
}
</script>

<style scoped>
.modern-card { border-radius: 12px !important; font-family: 'Outfit', sans-serif !important; }
.bg-grey-light { background-color: #f8fafc !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.06) !important; }
.border-white-sm { border: 2px solid white !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.05) !important; }
.h-44 { height: 44px !important; }
.h-12 { height: 48px !important; }
.opacity-10 { opacity: 0.1; }
.lh-1 { line-height: 1 !important; }
.tracking-widest { letter-spacing: 1.5px; }
.tracking-wider { letter-spacing: 0.8px; }
</style>